// cspell:ignore Couldn
import {
  Box,
  Flex,
  HStack,
  Icon,
  Skeleton,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { HiOutlineRefresh } from "react-icons/hi";
import {
  HiOutlineExclamationCircle,
  HiOutlineInformationCircle,
} from "react-icons/hi2";

import { Button } from "../../../../components";
import { GradientSparklesIcon } from "../../../../components/Icons/GradientSparklesIcon";
import { bluePurpleGradient } from "./constants";
import {
  GuideQuestionSuggestionsBucketV2,
  GuideQuestionSuggestionV2,
  SuggestionConflictReason,
} from "./types";

const GUIDE_ASSISTANT_COLUMN_WIDTH = "456px";
const GUIDE_ASSISTANT_STICKY_TOP = "82px";
const GUIDE_ASSISTANT_HEIGHT = "calc(100vh - 170px)";
const ALERT_BORDER_COLOR = "#B38100";
const ALERT_BACKGROUND_COLOR = "#FFF8DB";

export type GuideAssistantPanelState =
  | "loading"
  | "total-error"
  | "empty"
  | "ready";

const SuggestionBadge: React.FC<{
  label: string;
  isSelected: boolean;
}> = ({ label, isSelected }) => (
  <Box pt={0.5} flexShrink={0} w={6}>
    <Flex
      bg={isSelected ? undefined : "white"}
      background={isSelected ? bluePurpleGradient(0.1) : undefined}
      border="1px solid"
      borderColor={isSelected ? "transparent" : "gray.200"}
      borderRadius="3px"
      px={1}
      py={0.5}
      justify="center"
    >
      <Text
        fontSize="2xs"
        fontWeight={isSelected ? "600" : "500"}
        lineHeight="12px"
        color={isSelected ? "accent.600" : "gray.500"}
      >
        {label}
      </Text>
    </Flex>
  </Box>
);

type GuideAssistantPanelProps = {
  panelState: GuideAssistantPanelState;
  hasPartialError: boolean;
  questionSuggestions: GuideQuestionSuggestionsBucketV2[];
  selectedSuggestionId: string | null;
  ignoringSuggestionId: string | null;
  disabledSuggestionReasons: Record<string, SuggestionConflictReason>;
  isGuideStale: boolean;
  onSelectQuestion: (questionId: string, questionIndex: number) => void;
  onSelectSuggestion: (suggestion: GuideQuestionSuggestionV2) => void;
  onRefreshSuggestions: () => void;
  onHoverQuestion: (
    questionId: string | null,
    questionIndex: number | null
  ) => void;
  stickyTop?: string;
};

const GuideAssistantHelperText: React.FC = () => (
  <Box mt={4}>
    <Flex bg="gray.50" px={3} py={2} borderRadius="md">
      <Text fontSize="sm" color="gray.600" lineHeight="20px">
        Select a suggestion to preview the change in your guide.
      </Text>
    </Flex>
  </Box>
);

const GuideAssistantSkeletonTile: React.FC = () => (
  <Box
    data-testid="guide-assistant-skeleton-tile"
    border="1px solid"
    borderColor="gray.200"
    borderRadius="md"
    px={4}
    py={5}
  >
    <VStack align="stretch" spacing={4}>
      <Skeleton height="10px" width="70%" borderRadius="sm" />
      <Skeleton height="10px" width="100%" borderRadius="sm" />
      <Skeleton height="10px" width="100%" borderRadius="sm" />
    </VStack>
  </Box>
);

const GuideAssistantCallout: React.FC<{
  icon: typeof HiOutlineInformationCircle;
  title: string;
  body?: string;
  onRefreshSuggestions: () => void;
}> = ({ icon, title, body, onRefreshSuggestions }) => (
  <Flex
    gap={3}
    align="center"
    bg={ALERT_BACKGROUND_COLOR}
    border="1px solid"
    borderColor={ALERT_BORDER_COLOR}
    borderRadius="md"
    px={4}
    py={3}
  >
    <Icon
      as={icon}
      boxSize={9}
      color={ALERT_BORDER_COLOR}
      flexShrink={0}
      strokeWidth={1}
    />
    <Box flex={1}>
      <Text fontWeight="600" fontSize="md" color="gray.900">
        {title}
      </Text>
      {body && (
        <Text fontSize="sm" color="gray.800">
          {body}
        </Text>
      )}
      <Button
        size="prismSm"
        variant="newPrismTertiary"
        minW="auto"
        borderColor="gray.700"
        mt={2}
        leftIcon={<HiOutlineRefresh />}
        onClick={onRefreshSuggestions}
      >
        Try again
      </Button>
    </Box>
  </Flex>
);

const GuideAssistantPanel: React.FC<GuideAssistantPanelProps> = ({
  panelState,
  hasPartialError,
  questionSuggestions,
  selectedSuggestionId,
  ignoringSuggestionId,
  disabledSuggestionReasons,
  isGuideStale,
  onSelectQuestion,
  onSelectSuggestion,
  onRefreshSuggestions,
  onHoverQuestion,
  stickyTop,
}) => {
  const suggestionBuckets = questionSuggestions.filter(
    (bucket) => bucket.suggestions.length > 0
  );

  return (
    <Box
      border="1px solid"
      borderColor="gray.200"
      borderRadius="xl"
      p={6}
      w={GUIDE_ASSISTANT_COLUMN_WIDTH}
      boxSizing="border-box"
      bg="white"
      h={GUIDE_ASSISTANT_HEIGHT}
      display="flex"
      flexDirection="column"
      position="sticky"
      top={stickyTop ?? GUIDE_ASSISTANT_STICKY_TOP}
      alignSelf="flex-start"
      overflow="hidden"
      flexShrink={0}
    >
      <HStack
        spacing={2}
        color="blue.600"
        pb={4}
        position="relative"
        sx={{
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "1px",
            background: bluePurpleGradient(0.2),
          },
        }}
      >
        <GradientSparklesIcon />
        <Text
          fontSize="16.8px"
          fontWeight="semibold"
          lineHeight="24px"
          background={bluePurpleGradient(1)}
          bgClip="text"
          sx={{
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Guide assistant
        </Text>
      </HStack>

      {panelState !== "total-error" && <GuideAssistantHelperText />}

      {panelState === "loading" && (
        <Box mt={4} flex="1" minH={0} overflowY="auto" pr={1}>
          <VStack align="stretch" spacing={5}>
            <GuideAssistantSkeletonTile />
            <GuideAssistantSkeletonTile />
            <GuideAssistantSkeletonTile />
          </VStack>
        </Box>
      )}

      {panelState === "total-error" && (
        <Flex flex="1" align="center" justify="center">
          <VStack spacing={6}>
            <Icon
              as={HiOutlineExclamationCircle}
              boxSize={14}
              color="yellow.700"
            />
            <Text
              fontSize="2xl"
              fontWeight="700"
              lineHeight="32px"
              color="gray.900"
              textAlign="center"
            >
              Couldn&apos;t load new suggestions.
            </Text>
            <Button
              size="prismMd"
              variant="newPrismTertiary"
              borderColor="gray.700"
              leftIcon={<HiOutlineRefresh />}
              onClick={onRefreshSuggestions}
            >
              Try again
            </Button>
          </VStack>
        </Flex>
      )}

      {panelState === "empty" && (
        <Flex flex="1" align="center" justify="center" px={6}>
          <Text fontSize="sm" color="gray.600" textAlign="center">
            No suggestions to review right now.
          </Text>
        </Flex>
      )}

      {panelState === "ready" && (
        <>
          <Box mt={4} flex="1" minH={0} overflowY="auto" pr={1} pb={4}>
            <VStack align="stretch" spacing={5}>
              {suggestionBuckets.map((bucket, bucketIndex) => {
                return (
                  <Box key={bucket.questionId}>
                    <Flex
                      mb={2}
                      mt={bucketIndex > 0 ? 1 : 0}
                      align="center"
                      gap={2}
                      cursor="pointer"
                      onClick={() =>
                        onSelectQuestion(
                          bucket.questionId,
                          bucket.questionIndex
                        )
                      }
                    >
                      <Text
                        color="gray.500"
                        fontSize="xs"
                        fontWeight="600"
                        lineHeight="16px"
                        textTransform="uppercase"
                      >
                        {`Question ${bucket.questionIndex + 1}`}
                      </Text>
                    </Flex>

                    <VStack align="stretch" spacing={2}>
                      {bucket.suggestions.map((suggestion) => {
                        const isSelected =
                          selectedSuggestionId === suggestion.id;
                        const isIgnoring =
                          ignoringSuggestionId === suggestion.id;
                        const conflictReason =
                          disabledSuggestionReasons[suggestion.id];
                        const isConflictDisabled = !!conflictReason;
                        const isDisabled = isIgnoring || isConflictDisabled;

                        const tooltipLabel =
                          conflictReason === "accepted-change-conflict"
                            ? "Conflicts with an accepted change"
                            : conflictReason === "partial-application-lock"
                            ? "Finish reviewing the current suggestion first"
                            : conflictReason === "selected-suggestion-conflict"
                            ? "Conflicts with the selected suggestion"
                            : "";
                        const displayTitle = suggestion.title.trim();
                        const displayDescription =
                          suggestion.description.trim() || null;

                        return (
                          <Tooltip
                            key={suggestion.id}
                            label={tooltipLabel}
                            hasArrow
                            isDisabled={!isConflictDisabled}
                          >
                            <Box
                              border="1px solid"
                              borderColor={
                                isSelected ? "transparent" : "gray.200"
                              }
                              bg={isSelected ? undefined : "white"}
                              background={
                                isSelected
                                  ? bluePurpleGradient(0.05)
                                  : undefined
                              }
                              borderRadius="md"
                              px={4}
                              py={3}
                              position="relative"
                              cursor={isDisabled ? "not-allowed" : "pointer"}
                              transition="all 0.15s ease"
                              opacity={isConflictDisabled ? 0.55 : 1}
                              sx={
                                isSelected
                                  ? {
                                      "&::before": {
                                        content: '""',
                                        position: "absolute",
                                        inset: 0,
                                        borderRadius: "inherit",
                                        padding: "1px",
                                        background: bluePurpleGradient(0.6),
                                        mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                                        maskComposite: "exclude",
                                        WebkitMaskComposite: "xor",
                                        pointerEvents: "none",
                                      },
                                    }
                                  : undefined
                              }
                              onMouseEnter={() => {
                                if (isConflictDisabled) {
                                  return;
                                }
                                onHoverQuestion(
                                  bucket.questionId,
                                  bucket.questionIndex
                                );
                              }}
                              onMouseLeave={() => onHoverQuestion(null, null)}
                              onClick={() => {
                                if (!isDisabled) {
                                  onSelectSuggestion(suggestion);
                                }
                              }}
                              _hover={
                                isDisabled || isSelected
                                  ? undefined
                                  : {
                                      borderColor: "#0074DD",
                                      background: bluePurpleGradient(0.1),
                                    }
                              }
                            >
                              <VStack align="stretch" spacing={2}>
                                <Flex gap={3} align="start">
                                  <SuggestionBadge
                                    label="TIP"
                                    isSelected={isSelected}
                                  />
                                  <Text
                                    color="gray.800"
                                    fontSize="sm"
                                    fontWeight="600"
                                    lineHeight="20px"
                                    flex={1}
                                  >
                                    {displayTitle}
                                  </Text>
                                </Flex>
                                {displayDescription && (
                                  <Flex gap={3} align="start">
                                    <SuggestionBadge
                                      label="FIX"
                                      isSelected={isSelected}
                                    />
                                    <Text
                                      color="gray.800"
                                      fontSize="sm"
                                      fontWeight="normal"
                                      lineHeight="20px"
                                      flex={1}
                                    >
                                      {displayDescription}
                                    </Text>
                                  </Flex>
                                )}
                              </VStack>
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </VStack>
                  </Box>
                );
              })}
            </VStack>
          </Box>

          {(isGuideStale || hasPartialError) && (
            <VStack align="stretch" spacing={4}>
              {isGuideStale && (
                <Flex
                  gap={3}
                  align="center"
                  bg="gray.100"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  px={4}
                  py={3}
                >
                  <Icon
                    as={HiOutlineInformationCircle}
                    boxSize={9}
                    color="gray.700"
                    flexShrink={0}
                    strokeWidth={1}
                  />
                  <Box flex={1}>
                    <Text fontWeight="600" fontSize="md" color="gray.900">
                      Your interview guide was updated.
                    </Text>
                    <Button
                      size="prismSm"
                      variant="newPrismTertiary"
                      minW="auto"
                      borderColor="gray.700"
                      mt={2}
                      leftIcon={<HiOutlineRefresh />}
                      onClick={onRefreshSuggestions}
                    >
                      Check for new suggestions
                    </Button>
                  </Box>
                </Flex>
              )}

              {hasPartialError && (
                <GuideAssistantCallout
                  icon={HiOutlineExclamationCircle}
                  title="New suggestions are incomplete."
                  body="Some of them failed to load."
                  onRefreshSuggestions={onRefreshSuggestions}
                />
              )}
            </VStack>
          )}
        </>
      )}
    </Box>
  );
};

export default GuideAssistantPanel;
